import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Tabs } from "antd";
import "../assets/style.css";
import { ExSummary } from "../components/ExSummary";
import { WorkHistory } from "../components/WorkHistory";
import { EducationHistory } from "../components/EducationHistory";
import { Certifications } from "../components/Certifications";
import { Invite } from "../components/Invite";
import { AppButton } from "components/button/AppButton";
import { SendInvite } from "../components/SendInvite";
import { useState } from "react";
import { ReportTalent } from "../components/ReportTalent";
import { useParams } from "react-router-dom";
import { useGetSingleTalent } from "../hooks/useFetchTalentDetails";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import avatar from "assets/images/default.webp";
import { Projects } from "../components/Projects";
import { Navbar } from "components/layout/components/Navbar";

const DEFAULT_COVER = "/assets/cover_image.png";

export const TalentDetails = () => {
  const params = useParams();
  const id = params.id;
  const { token } = useGetUserInfo();
  const [inviteModal, setInviteModal] = useState(false);
  const [reportModaL, setReportModaL] = useState(false);

  const { data: talentData, isLoading } = useGetSingleTalent({
    token,
    id: id as unknown as string,
  });
  const data = talentData?.data;

  const fullName = [data?.first_name, data?.middle_name, data?.last_name]
    .filter((n) => n && n !== "null")
    .join(" ");

  const skillsList: string[] = Array.isArray((data as any)?.skills)
    ? (data as any).skills.flatMap((s: string) =>
        s?.includes(",") ? s.split(",").map((x) => x.trim()) : [s],
      )
    : [];

  return (
    <DashboardLayout>
      <Navbar title="Talents" description="View and manage Talents" />

      <SendInvite
        open={inviteModal}
        handleClose={() => setInviteModal(false)}
        id={data?.id}
      />
      <ReportTalent
        id={data?.id ? data.id : []}
        open={reportModaL}
        handleClose={() => setReportModaL(false)}
      />

      <div className="Container">
        <img
          src={(data as any)?.user?.cover_photo || DEFAULT_COVER}
          alt="bg"
          className="w-full h-full object-cover"
        />

        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="flex items-start gap-3 pl-5">
            <img
              src={data?.dp ?? avatar}
              alt="avatar"
              className="h-28 w-28 rounded-full object-cover object-top border-2 border-white -mt-7"
            />
            <div className="pt-3">
              <h3 className="font-semibold text-lg pb-1">{fullName}</h3>

              {(data as any)?.current_position && (
                <p className="text-sm text-gray-600 pb-1">
                  {(data as any).current_position}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                {(data as any)?.current_location && (
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line" />
                    {(data as any).current_location}
                  </span>
                )}
                {data?.email && (
                  <span className="flex items-center gap-1">
                    <i className="ri-mail-line" />
                    {data.email}
                  </span>
                )}
                {data?.phone && (
                  <span className="flex items-center gap-1">
                    <i className="ri-phone-line" />
                    {data.phone}
                  </span>
                )}
                {(data as any)?.urls?.linkedin && (
                  <a
                    href={(data as any).urls.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <i className="ri-linkedin-line" />
                    LinkedIn
                  </a>
                )}
                {(data as any)?.urls?.portfolio && (
                  <a
                    href={(data as any).urls.portfolio}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <i className="ri-briefcase-line" />
                    Portfolio
                  </a>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex gap-x-3 justify-start md:justify-end mt-3">
              <AppButton
                label="Report"
                variant="transparent"
                handleClick={() => setReportModaL(true)}
              />
              <AppButton
                label="Send Invite"
                handleClick={() => setInviteModal(true)}
              />
            </div>
          </div>
        </div>

        <Tabs defaultActiveKey="executive-summary" size="large">
          <Tabs.TabPane
            tab="Executive Summary"
            key="executive-summary"
            className="tabBg"
          >
            <ExSummary isLoading={isLoading} data={data as any} />

            <div className="mt-6">
              <h4 className="text-base font-semibold text-gray-800 mb-3">
                Skills & Competencies
              </h4>
              <div className="flex flex-wrap gap-2">
                {skillsList.map((skill, idx) => (
                  <span
                    key={idx}
                    className="text-sm bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-md"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Work Experience" key="work" className="tabBg">
            <WorkHistory
              isLoading={isLoading}
              data={(data as any)?.work_histories ?? []}
              volunteer={(data as any)?.volunteer_experiences ?? []}
            />
          </Tabs.TabPane>
          <Tabs.TabPane
            tab="Education History"
            key="education"
            className="tabBg"
          >
            <EducationHistory
              isLoading={isLoading}
              data={(data as any)?.education_histories ?? []}
            />
          </Tabs.TabPane>
          <Tabs.TabPane
            tab="Certifications"
            key="certifications"
            className="tabBg"
          >
            <Certifications
              isLoading={isLoading}
              certifications={(data as any)?.certifications ?? []}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Invite" key="invite" className="tabBg">
            <Invite
              invitations={(data as any)?.invitations ?? []}
              isLoading={isLoading}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Projects" key="projects" className="tabBg">
            {(data as any)?.projects?.length ? (
              <Projects data={(data as any).projects} />
            ) : (
              <p className="text-sm text-gray-500">No projects added.</p>
            )}
          </Tabs.TabPane>
          <Tabs.TabPane tab="URLs" key="url" className="tabBg">
            <div className="space-y-3">
              {(data as any)?.urls?.linkedin && (
                <a
                  href={(data as any).urls.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <i className="ri-linkedin-line text-lg" />
                  LinkedIn
                </a>
              )}

              {(data as any)?.urls?.portfolio && (
                <a
                  href={(data as any).urls.portfolio}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <i className="ri-briefcase-line text-lg" />
                  Portfolio
                </a>
              )}

              {(data as any)?.urls?.github && (
                <a
                  href={(data as any).urls.github}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <i className="ri-github-line text-lg" />
                  GitHub
                </a>
              )}

              {(data as any)?.urls?.behance && (
                <a
                  href={(data as any).urls.behance}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <i className="ri-behance-line text-lg" />
                  Behance
                </a>
              )}

              {(data as any)?.urls?.facebook && (
                <a
                  href={(data as any).urls.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <i className="ri-facebook-line text-lg" />
                  Facebook
                </a>
              )}

              {(data as any)?.urls?.x && (
                <a
                  href={(data as any).urls.x}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <i className="ri-twitter-x-line text-lg" />X (Twitter)
                </a>
              )}

              {(data as any)?.urls?.instagram && (
                <a
                  href={(data as any).urls.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline text-sm"
                >
                  <i className="ri-instagram-line text-lg" />
                  Instagram
                </a>
              )}

              {!(
                (data as any)?.urls?.linkedin ||
                (data as any)?.urls?.portfolio ||
                (data as any)?.urls?.github ||
                (data as any)?.urls?.behance ||
                (data as any)?.urls?.facebook ||
                (data as any)?.urls?.x ||
                (data as any)?.urls?.instagram
              ) && (
                <p className="text-sm text-gray-500">
                  No URLs available for this talent.
                </p>
              )}
            </div>
          </Tabs.TabPane>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};
